import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useQuery } from "@tanstack/vue-query";
import axios from "axios";
import {
  type BidDetailsProduct,
  type MaxBid,
  MeApi,
  type ProductDetails,
  ProductListingsBidsApi,
} from "mws-ts-v2-sdk";
import { unref } from "vue";

let meApi: MeApi;
const getMeApi = () => {
  // reduce memory overhead by only using one instance
  if (!meApi) {
    meApi = new MeApi(
      undefined,
      useRuntimeConfig().public.catalogServiceUrl,
      axios,
    );
  }

  return meApi;
};

let productListingsBidApi: ProductListingsBidsApi;
const getProductListingsBidsApi = () => {
  // reduce memory overhead by only using one instance
  if (!productListingsBidApi) {
    productListingsBidApi = new ProductListingsBidsApi(
      undefined,
      useRuntimeConfig().public.catalogServiceUrl,
      axios,
    );
  }

  return productListingsBidApi;
};

export const bidQueryKeys = createQueryKeys("bids", {
  forCurrentUser: (
    userId: MaybeRef<string>,
    platformId: MaybeRef<string>,
    filters?: MaybeRef<{ pageSize: number; offset: number }>,
  ) => ({
    queryKey: [userId, filters],
    queryFn: async () => {
      const response = await getMeApi().v2MeBidsGet({
        xPlatformKey: unref(platformId),
        pageSize: unref(filters)?.pageSize || 1000,
        offset: unref(filters)?.offset || 0,
      });

      return response.data;
    },
    contextQueries: {
      products: (
        productsFilters?: MaybeRef<{ pageSize: number; offset: number }>,
      ) => ({
        queryKey: [productsFilters || "defaultPagination"],
        queryFn: async () => {
          const response = await getMeApi().v2MeBidsProductsGet({
            xPlatformKey: unref(platformId),
            pageSize: unref(productsFilters)?.pageSize || 25,
            offset: unref(productsFilters)?.offset || 0,
          });

          return response.data;
        },
      }),
    },
  }),
  max: (
    productId: MaybeRefOrGetter<string>,
    platformId: MaybeRefOrGetter<string>,
  ) => ({
    queryKey: [productId],
    queryFn: async () => {
      const response =
        await getProductListingsBidsApi().v2ProductListingsProductListingIdBidsMaxGet(
          {
            productListingId: toValue(productId),
            xPlatformKey: toValue(platformId),
          },
        );

      // In case of no max bid, this endpoint will return a 204 no content.
      // We fallback to null
      return (response.data ?? null) as MaxBid | null;
    },
  }),
});

export const useActiveMaxBidQuery = (
  product: MaybeRefOrGetter<ProductDetails | BidDetailsProduct>,
) => {
  const { isLoggedIn } = useAuth();

  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );

  const productId = computed(() => toValue(product).id);

  const enabled = computed(() => {
    const productValue = toValue(product);

    return (
      isLoggedIn.value &&
      // BidDetailsProduct doesn't contain this feature flag yet.
      // If we want to add max bid management to my account we should start there.
      "allowMaxBidding" in productValue &&
      productValue.allowMaxBidding
    );
  });

  return useQuery({
    ...bidQueryKeys.max(productId, platformId),
    enabled,
    // Match the refetch interval of the product detail to ensure we're staying up to date
    refetchInterval: 60000,
  });
};
